import { createContext, forwardRef, useMemo, useEffect, useRef, ReactElement } from "react";
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_WIDTH_CLOSED } from "../../Components/Sidebar/sidebar";
import { Box, Breakpoint, styled, SxProps, Theme } from "@mui/material";
import BasketPanel, { BASKETPANEL_WIDTH } from "../../Components/Basket/BasketPanel/basket-panel";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";

import React from "react";
import { MetadataFieldValueObjectTypes } from "../../Core/Api/Types/metadata-field-value-object-types";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { useDispatch, useSelector } from "react-redux";
import { GetSidebarOpen, ToggleSidebar } from "../../Redux/Reducers/redux-slice-settings-users";
import { cacheService } from "../../Core/Api/services";
import PageHead from "../../Components/Topbars/DashboardTopbar/page-head";
import LayoutCenterPanel from "../Dashboard-v2/layout-panel";
import { GetDrawerOpen, GetLeftPanelInfos, GetRightPanelInfos } from "../../Redux/Reducers/redux-slice-layout";
import SidePanelLayout from "../Dashboard-v2/side-panel-layout";
import { useLocation } from "react-router";
import "./dashboard.css";
export interface DashboardLayoutProps {
  children?: React.ReactNode;
  Subbar?: React.ReactNode;
  Banner?: React.ReactNode;
  Heading?: React.ReactNode;
  TopbarLeft?: React.ReactNode;
  ref?: React.ForwardedRef<HTMLDivElement>;
  showStickiesOnScroll?: boolean;
  stickiesVisibleAt?: number;
  showBanner?: boolean;
  withContainer?: boolean;
  maxWidth?: false | Breakpoint | undefined;
  showTopbarSearchOnScroll?: boolean;
  transparentTopbar?: boolean;
  sxContent?: SxProps<Theme>;
  setOpenSidebarFallback?: React.Dispatch<React.SetStateAction<boolean>>;
  noScroll?: boolean;
  panelHighlight?: boolean;
}

export class FilterContextClass {
  public openFiltersPanel: boolean = false;
  public openFiltersPanelLayer: ObjectLayer | null = ObjectLayer.Title;
  public openFiltersPanelType: MetadataFieldValueObjectTypes = MetadataFieldValueObjectTypes.DbAssetObjectInfos;
  public openFiltersPanelMetadataFields: DbMetadataFieldInfos[] | undefined = undefined;

  setOpenFiltersPanel(value: boolean): void {
    this.openFiltersPanel = value;
  }

  setOpenFiltersPanelLayer(value: ObjectLayer | null): void {
    this.openFiltersPanelLayer = value;
  }

  setOpenFiltersPanelType(value: MetadataFieldValueObjectTypes): void {
    this.openFiltersPanelType = value;
  }

  setOpenFiltersPanelMetadataFields(value: DbMetadataFieldInfos[] | undefined): void {
    this.openFiltersPanelMetadataFields = value;
  }
}

export const FilterContext = createContext(new FilterContextClass());

export const BasketContext = createContext({
  setOpenBasket: (open: boolean) => {},
  openBasket: false,
});

export const LayoutContext = createContext({
  setOpenSidebar: (open: boolean) => {},
  setVisibleBanner: (open: boolean) => {},
  openSidebar: true,
  visibleBanner: false,
});

export const minSliderWidthLeft = 150;
export const minSliderWidthRight = 225;
export const maxSliderWidth = 850;
export const pinnedSliderWidth = 16;

const DashboardLayout = forwardRef<HTMLDivElement | null, DashboardLayoutProps>(
  (
    {
      setOpenSidebarFallback,
      children,
      withContainer = false,
      maxWidth = "xl",
      Banner,
      Heading,
      showBanner = false,
      sxContent = {},
      noScroll = false,
      panelHighlight = true,
    },
    ref
  ) => {
    const dispatch = useDispatch();

    const [openBasket, setOpenBasket] = React.useState(false);
    const [headingCollapsed, setHeadingCollapsed] = React.useState<boolean>(false);
    const [visibleBanner, setVisibleBanner] = React.useState(showBanner);

    const openSidebar = useSelector(GetSidebarOpen);
    const leftPanel = useSelector(GetLeftPanelInfos);
    const rightPanel = useSelector(GetRightPanelInfos);
    const drawerOpen = useSelector(GetDrawerOpen);
    const { pathname } = useLocation();

    const toggleSidebar = () => {
      dispatch(ToggleSidebar());
    };

    const globalContainerRef = useRef<any>(undefined);

    useEffect(() => {
      const cachedOpenSidebar = cacheService.GetDrawerOpen();
      if (cachedOpenSidebar !== openSidebar) toggleSidebar();
    }, []);

    useEffect(() => {
      if (setOpenSidebarFallback) setOpenSidebarFallback(openSidebar);
      cacheService.SetDrawerOpen(openSidebar);
    }, [openSidebar]);

    useEffect(() => {
      if (pathname.indexOf("preview") > -1) {
        setHeadingCollapsed(true);
      } else {
        setHeadingCollapsed(false);
      }
    }, [pathname]);

    const leftBar = useMemo(() => {
      return <Sidebar open={openSidebar} onUpdate={(newOpen) => toggleSidebar()} />;
    }, [openSidebar]);

    const basket = useMemo(() => {
      return <BasketPanel />;
    }, []);

    const appContentWidth = useMemo(
      () =>
        `calc(100% - ${
          openSidebar
            ? openBasket
              ? SIDEBAR_WIDTH + BASKETPANEL_WIDTH
              : SIDEBAR_WIDTH
            : openBasket
            ? SIDEBAR_WIDTH_CLOSED + BASKETPANEL_WIDTH
            : SIDEBAR_WIDTH_CLOSED
        }px)`,
      [openBasket, openSidebar]
    );
    return (
      <LayoutContext.Provider
        value={{
          setOpenSidebar: toggleSidebar,
          setVisibleBanner,
          openSidebar,
          visibleBanner,
        }}
      >
        <BasketContext.Provider
          value={{
            setOpenBasket,
            openBasket,
          }}
        >
          <StyledPageWrapper ref={ref}>
            {leftBar}

            <Box className="app-content-wrapper" ref={globalContainerRef} width={appContentWidth}>
              <PageHead />
              {Heading &&
                React.cloneElement(
                  Heading as ReactElement,
                  {
                    collapsed: headingCollapsed,
                  } as any
                )}
              <div
                className={"app-panels-wrapper"}
                style={{
                  borderRadius: visibleBanner && Banner ? "0px 0px 24px 24px" : "12px 24px",
                  ...(drawerOpen && {
                    filter: "blur(6px)",
                    transform: "scale(0.95)",
                  }),
                }}
              >
                <SidePanelLayout panel={leftPanel} position="left" />
                <LayoutCenterPanel
                  children={children}
                  withContainer={withContainer}
                  maxWidth={maxWidth}
                  sxContent={sxContent}
                  leftPanel={leftPanel}
                  rightPanel={rightPanel}
                  visibleBanner={visibleBanner}
                  Banner={Banner}
                  noScroll={noScroll}
                  hasHeading={Heading ? true : false}
                  setHeadingCollapsed={setHeadingCollapsed}
                  panelHighlight={panelHighlight}
                />
                <SidePanelLayout panel={rightPanel} position="right" />
              </div>
            </Box>
            {basket}
          </StyledPageWrapper>
        </BasketContext.Provider>
      </LayoutContext.Provider>
    );
  }
);

const StyledPageWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  overflowX: "hidden",
  background: theme.palette.background.page,
}));

export default DashboardLayout;
